import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";

import { CommonConstants, TextConstants } from "@shared/constants";
import { FileExtensionEnum } from "@shared/enums";
import { NotificationService } from "@shared/services";

import { RiskAssessmentTemplateImportService } from "../services/risk-assessment-template-import.service";

@Component({
  standalone: false,
  templateUrl: "./import-risk-assessment-template-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportRiskAssessmentTemplateDialogComponent {
  private readonly dialogRef: MatDialogRef<ImportRiskAssessmentTemplateDialogComponent> =
    inject(MatDialogRef);

  private readonly notificationService = inject(NotificationService);

  private readonly importService = inject(RiskAssessmentTemplateImportService);

  readonly isUploading = signal(false);

  readonly maxSizeInMb = CommonConstants.MAX_JSON_SIZE_IN_MB;

  readonly supportedFileTypes = [FileExtensionEnum.JSON];

  readonly uploadBoxFooterLeftText = $localize`:@@import-risk-assessment-template.upload.allowedfileextensions:Upload a (.json) file with template data in json format`;

  readonly uploadBoxFooterRightText = $localize`:@@shared.upload.jsonmaxsize:Maximum size: ${this.maxSizeInMb}:maxSizeInMb:MB`;

  onFileSelected(file: File) {
    this.isUploading.set(true);

    const reader = new FileReader();

    reader.readAsText(file);

    reader.onload = async () => {
      try {
        const result = JSON.parse(reader.result.toString());
        const isValid = await this.importService.isValid(result);

        if (!isValid) {
          return this.notificationService.showError(TextConstants.FILE_NOT_SUPPORTED_ERROR);
        }

        this.notificationService.showSuccess(
          $localize`:@@import-risk-assessment-template.success:Risk assessment template uploaded`,
        );

        this.close(true);
      } catch {
        this.notificationService.showError(TextConstants.FILE_NOT_SUPPORTED_ERROR);
      } finally {
        this.isUploading.set(false);
      }
    };
  }

  close(uploaded = false) {
    this.dialogRef.close(uploaded);
  }
}
